import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Stack,
    Chip,
    Button,
    IconButton,
    Tooltip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    CircularProgress,
    Alert,
    Snackbar,
    Avatar,
    Divider
} from '@mui/material';
import {
    Topic as TopicIcon,
    Quiz as QuestionIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    VisibilityOff as HideIcon,
    Assignment as FormIcon,
    Save as SaveIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';

const TopicManagement = ({ selectedCourse, apiClient }) => {
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    // Dialog states
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState(null);
    
    // Form states
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        is_published: false
    });
    const [formLoading, setFormLoading] = useState(false);

    // Fetch topics for the course
    const fetchTopics = async () => {
        if (!apiClient || !selectedCourse) return;

        setLoading(true);
        try {
            const response = await apiClient.get(`/api/courses/${selectedCourse.id}/topics`);
            setTopics(response.topics || response.data?.topics || []);
        } catch (error) {
            console.error('Failed to fetch topics:', error);
            setSnackbar({
                open: true,
                message: 'Failed to load topics',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Load topics when component mounts
    useEffect(() => {
        fetchTopics();
    }, [selectedCourse]);

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Reset form data
    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            is_published: false
        });
    };

    // Handle create topic
    const handleCreateTopic = () => {
        resetForm();
        setCreateDialogOpen(true);
    };

    // Handle edit topic
    const handleEditTopic = (topic) => {
        setSelectedTopic(topic);
        setFormData({
            name: topic.name || '',
            description: topic.description || '',
            is_published: topic.is_published || false
        });
        setEditDialogOpen(true);
    };

    // Handle save new topic
    const handleSaveNewTopic = async () => {
        if (!apiClient || !selectedCourse) return;

        setFormLoading(true);
        try {
            const topicData = {
                ...formData,
                course_id: selectedCourse.id
            };

            const newTopic = await apiClient.post(`/api/courses/${selectedCourse.id}/topics`, topicData);
            
            // Add the new topic to the list
            setTopics(prev => [...prev, newTopic]);
            
            setSnackbar({
                open: true,
                message: 'Topic created successfully!',
                severity: 'success'
            });
            
            setCreateDialogOpen(false);
            resetForm();
        } catch (error) {
            console.error('Failed to create topic:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to create topic',
                severity: 'error'
            });
        } finally {
            setFormLoading(false);
        }
    };

    // Handle save topic changes
    const handleSaveTopicChanges = async () => {
        if (!apiClient || !selectedTopic || !selectedCourse) return;

        setFormLoading(true);
        try {
            const updatedTopic = await apiClient.put(
                `/api/courses/${selectedCourse.id}/topics/${selectedTopic.id}`, 
                formData
            );
            
            // Update the topic in the list
            setTopics(prev => prev.map(topic => 
                topic.id === selectedTopic.id ? updatedTopic : topic
            ));
            
            setSnackbar({
                open: true,
                message: 'Topic updated successfully!',
                severity: 'success'
            });
            
            setEditDialogOpen(false);
            setSelectedTopic(null);
            resetForm();
        } catch (error) {
            console.error('Failed to update topic:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to update topic',
                severity: 'error'
            });
        } finally {
            setFormLoading(false);
        }
    };

    // Handle delete topic
    const handleDeleteTopic = async (topic) => {
        if (!apiClient || !selectedCourse) return;
        
        if (!window.confirm(`Are you sure you want to delete "${topic.name}"? This will also delete any associated forms and questions.`)) {
            return;
        }

        try {
            await apiClient.delete(`/api/courses/${selectedCourse.id}/topics/${topic.id}`);
            
            // Remove topic from list
            setTopics(prev => prev.filter(t => t.id !== topic.id));
            
            setSnackbar({
                open: true,
                message: 'Topic deleted successfully!',
                severity: 'success'
            });
        } catch (error) {
            console.error('Failed to delete topic:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to delete topic',
                severity: 'error'
            });
        }
    };

    // Handle toggle topic publish status
    const handleTogglePublish = async (topic) => {
        if (!apiClient || !selectedCourse) return;

        try {
            const updatedTopic = await apiClient.put(
                `/api/courses/${selectedCourse.id}/topics/${topic.id}`,
                { is_published: !topic.is_published }
            );
            
            // Update the topic in the list
            setTopics(prev => prev.map(t => 
                t.id === topic.id ? updatedTopic : t
            ));
            
            setSnackbar({
                open: true,
                message: `Topic ${updatedTopic.is_published ? 'published' : 'unpublished'} successfully!`,
                severity: 'success'
            });
        } catch (error) {
            console.error('Failed to toggle topic status:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Failed to update topic status',
                severity: 'error'
            });
        }
    };

    // Close snackbar
    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            return new Date(timestamp).toLocaleDateString();
        } catch {
            return 'N/A';
        }
    };

    return (
        <Box sx={{p: 2, height: '100%', overflow: 'auto'}}>
            {/* Header */}
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3}}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar>
                        <TopicIcon/>
                    </Avatar>
                    <Box>
                        <Typography variant="h6">
                            Topic Management
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage topics for "{selectedCourse?.title}"
                        </Typography>
                    </Box>
                </Stack>
                <Button
                    variant="contained"
                    startIcon={<AddIcon/>}
                    onClick={handleCreateTopic}
                >
                    Add Topic
                </Button>
            </Box>

            {/* Topics Table */}
            <Paper variant="outlined">
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Topic Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Form</TableCell>
                                <TableCell>Questions</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{py: 4}}>
                                        <CircularProgress/>
                                        <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                                            Loading topics...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : topics.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{py: 4}}>
                                        <Typography variant="body1" color="text.secondary">
                                            No topics found for this course
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon/>}
                                            onClick={handleCreateTopic}
                                            sx={{mt: 1}}
                                        >
                                            Create First Topic
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                topics.map((topic) => (
                                    <TableRow key={topic.id} hover>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {topic.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {topic.description}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={topic.is_published ? 'Published' : 'Draft'}
                                                color={topic.is_published ? 'success' : 'default'}
                                                size="small"
                                                icon={topic.is_published ? <ViewIcon/> : <HideIcon/>}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={topic.has_form ? 'Yes' : 'No'}
                                                color={topic.has_form ? 'info' : 'default'}
                                                size="small"
                                                icon={<FormIcon/>}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={topic.question_count || 0}
                                                size="small"
                                                icon={<QuestionIcon/>}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatTimestamp(topic.created_at)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5}>
                                                <Tooltip title={topic.is_published ? 'Unpublish' : 'Publish'}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleTogglePublish(topic)}
                                                        color={topic.is_published ? 'success' : 'default'}
                                                    >
                                                        {topic.is_published ? <ViewIcon/> : <HideIcon/>}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit Topic">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditTopic(topic)}
                                                    >
                                                        <EditIcon/>
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Topic">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteTopic(topic)}
                                                        color="error"
                                                    >
                                                        <DeleteIcon/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Create Topic Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h6">Create New Topic</Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            fullWidth
                            label="Topic Name"
                            value={formData.name}
                            onChange={(e) => handleFieldChange('name', e.target.value)}
                            placeholder="Enter topic name"
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Description"
                            value={formData.description}
                            onChange={(e) => handleFieldChange('description', e.target.value)}
                            placeholder="Enter topic description"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_published}
                                    onChange={(e) => handleFieldChange('is_published', e.target.checked)}
                                />
                            }
                            label="Publish immediately"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveNewTopic}
                        disabled={formLoading || !formData.name}
                        startIcon={formLoading ? <CircularProgress size={16}/> : <SaveIcon/>}
                    >
                        Create Topic
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Topic Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h6">Edit Topic</Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            fullWidth
                            label="Topic Name"
                            value={formData.name}
                            onChange={(e) => handleFieldChange('name', e.target.value)}
                            placeholder="Enter topic name"
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Description"
                            value={formData.description}
                            onChange={(e) => handleFieldChange('description', e.target.value)}
                            placeholder="Enter topic description"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_published}
                                    onChange={(e) => handleFieldChange('is_published', e.target.checked)}
                                />
                            }
                            label="Published"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveTopicChanges}
                        disabled={formLoading || !formData.name}
                        startIcon={formLoading ? <CircularProgress size={16}/> : <SaveIcon/>}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{width: '100%'}}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TopicManagement;